/* AFDS 2.0 hero carousel — event-driven per-slide scheduling */
(() => {
  const MAX_TIMEOUT = 2147483647;
  const init = (root = document) => {
    const sections = [];
    if (root.matches?.('[data-section-type="af-hero-carousel"]')) sections.push(root);
    root.querySelectorAll?.('[data-section-type="af-hero-carousel"]').forEach(s => sections.push(s));

    sections.forEach(section => {
      if (section.dataset.ready === 'true') return;
      section.dataset.ready = 'true';
      const slides = [...section.querySelectorAll('[data-carousel-slide]')];
      if (!slides.length) return;
      const viewport = section.querySelector('[data-carousel-viewport]');
      const nav = section.querySelector('.af-hero-carousel__nav');
      const prev = section.querySelector('[data-prev]');
      const next = section.querySelector('[data-next]');
      const counter = section.querySelector('[data-current]');
      const total = section.querySelector('[data-total]');
      const progress = section.querySelector('[data-progress]');
      const tabs = [...section.querySelectorAll('[data-go]')];
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const autoplay = section.dataset.autoplay === 'true' && !reduced;
      const duration = Number(section.dataset.duration) || 5000;
      let eligible = [], index = -1, timer = null, boundaryTimer = null;
      let firstRender = true;
      let startX = 0, startY = 0, resizeObserver;
      let hovering = false, focused = false;
      let editorSelection = -1;
      const pad = n => String(n).padStart(2, '0');
      const parseDate = value => {
        if (!value || !value.trim()) return NaN;
        // Require an explicit timezone, consistent with the Weekly Offer fields.
        if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(value.trim())) return NaN;
        return Date.parse(value.trim());
      };
      const bounds = slides.map(s => ({
        fallback: s.dataset.fallback === 'true',
        startEnabled: s.dataset.startEnabled === 'true',
        start: s.dataset.startEnabled === 'true' ? parseDate(s.dataset.start) : NaN,
        end: parseDate(s.dataset.end)
      }));

      const setHeight = () => {
        const active = slides[index];
        if (!active || !viewport) return;
        requestAnimationFrame(() => {
          if (!section.isConnected || slides[index] !== active) return;
          const frame = active.querySelector('.af-hero-render__frame') || active;
          const height = Math.ceil(frame.scrollHeight);
          if (height > 0) viewport.style.setProperty('--carousel-active-height', `${height}px`);
        });
      };
      const observeActive = () => {
        resizeObserver?.disconnect();
        const frame = slides[index]?.querySelector('.af-hero-render__frame');
        if (frame && 'ResizeObserver' in window) {
          resizeObserver = new ResizeObserver(setHeight);
          resizeObserver.observe(frame);
        }
      };
      const stopAutoplay = () => { clearInterval(timer); timer = null; };
      const restart = () => {
        stopAutoplay();
        if (autoplay && eligible.length > 1 && !hovering && !focused && !document.hidden)
          timer = setInterval(() => move(1), duration);
      };
      const render = () => {
        const position = eligible.indexOf(index);
        slides.forEach((slide, i) => {
          const selected = i === index && position !== -1;
          slide.classList.toggle('is-active', selected);
          slide.setAttribute('aria-hidden', String(!selected));
          slide.querySelectorAll('video').forEach(video => {
            if (selected) { const result = video.play(); result?.catch?.(() => {}); }
            else video.pause();
          });
        });
        tabs.forEach((tab, i) => {
          const positionInEligible = eligible.indexOf(i);
          tab.hidden = positionInEligible === -1;
          tab.classList.toggle('is-active', i === index && position !== -1);
          tab.setAttribute('aria-selected', String(i === index && position !== -1));
          const number = tab.querySelector('span');
          if (number && positionInEligible !== -1) number.textContent = pad(positionInEligible + 1);
        });
        if (counter) counter.textContent = pad(Math.max(0, position + 1));
        if (total) total.textContent = pad(eligible.length);
        if (progress) progress.style.width = eligible.length ? ((position + 1) / eligible.length * 100) + '%' : '0%';
        if (nav) nav.hidden = eligible.length <= 1;
        section.hidden = eligible.length === 0;
        // Release initial CSS gate only after eligible slides are selected.
        section.removeAttribute('data-schedule-pending');
        if (eligible.length === 0) viewport?.style.removeProperty('--carousel-active-height');
        observeActive();
        setHeight();
        restart();
      };
      const select = i => {
        if (!eligible.includes(i)) return;
        index = i;
        render();
      };
      const move = step => {
        if (eligible.length <= 1) return;
        const position = eligible.indexOf(index);
        select(eligible[(position + step + eligible.length) % eligible.length]);
      };
      const reschedule = () => {
        clearTimeout(boundaryTimer);
        if (!section.isConnected) return;
        const now = Date.now();
        let nextBoundary = Infinity;
        const available = [];
        bounds.forEach((b, i) => {
          if (Number.isFinite(b.start) && b.start > now) nextBoundary = Math.min(nextBoundary, b.start);
          if (Number.isFinite(b.end) && b.end > now) nextBoundary = Math.min(nextBoundary, b.end);
          // An enabled but missing/malformed start must not silently go live.
          if ((!b.startEnabled || (Number.isFinite(b.start) && now >= b.start)) && (!Number.isFinite(b.end) || now < b.end)) available.push(i);
        });
        // Fallbacks should never rotate alongside current campaign slides.
        const primary = available.filter(i => !bounds[i].fallback);
        const visible = primary.length ? primary : available.filter(i => bounds[i].fallback);
        const old = eligible;
        eligible = visible;
        // Preserve the currently visible slide where possible; otherwise use first eligible.
        const nextIndex = visible.includes(index) ? index : (visible[0] ?? -1);
        if (firstRender || index !== nextIndex || old.length !== visible.length || old.some((v, i) => v !== visible[i])) {
          index = nextIndex;
          firstRender = false;
          render();
        }
        if (Number.isFinite(nextBoundary)) {
          const remaining = nextBoundary - Date.now();
          boundaryTimer = setTimeout(reschedule, Math.max(1, Math.min(remaining + 15, MAX_TIMEOUT)));
        }
      };

      prev?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      tabs.forEach(tab => tab.addEventListener('click', () => select(Number(tab.dataset.go))));
      viewport?.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          if (eligible.length <= 1) return;
          e.preventDefault(); move(e.key === 'ArrowLeft' ? -1 : 1);
        }
      });
      viewport?.addEventListener('mouseenter', () => { hovering = true; stopAutoplay(); });
      viewport?.addEventListener('mouseleave', () => { hovering = false; restart(); });
      viewport?.addEventListener('focusin', () => { focused = true; stopAutoplay(); });
      viewport?.addEventListener('focusout', () => {
        setTimeout(() => { focused = !!viewport?.contains(document.activeElement); restart(); }, 0);
      });
      viewport?.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX; startY = e.touches[0].clientY;
      }, { passive: true });
      viewport?.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - startX;
        const dy = Math.abs(e.changedTouches[0].clientY - startY);
        if (Math.abs(dx) > 50 && dy < 100) move(dx > 0 ? -1 : 1);
      }, { passive: true });
      window.addEventListener('resize', setHeight, { passive: true });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) reschedule(); restart(); });
      window.addEventListener('pageshow', reschedule);
      section.addEventListener('shopify:block:select', e => {
        const slide = e.target.closest('[data-carousel-slide]');
        if (!slide) return;
        editorSelection = slides.indexOf(slide);
        // Theme editor can preview a scheduled-out slide without changing storefront rules.
        if (!eligible.includes(editorSelection)) {
          eligible = [...new Set([...eligible, editorSelection])].sort((a, b) => a - b);
        }
        select(editorSelection);
      });
      section.addEventListener('shopify:block:deselect', () => { editorSelection = -1; reschedule(); });
      reschedule();
    });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => init()) : init();
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
